<template>
  <div class="ag-grid-infinite-wrapper">
    <div class="ag-grid-infinite-table-wrapper" ref="tableWrapper">
      <ag-grid-vue
        ref="table"
        class="ag-grid-infinite-table"
        :style="autoHeight ? {} : tableStyles"
        :gridOptions="gridOptions"
        :defaultColDef="defaultColDef"
        :columnDefs="columnDefs"
        :rowData="rows"
        :rowHeight="rowHeight"
        :getRowHeight="getRowHeight"
        :headerHeight="headerHeight"
        :overlayLoadingTemplate="overlayLoadingTemplate"
        :overlayNoRowsTemplate="overlayNoRowsTemplate"
        :floatingFilter="filterable"
        :floatingFiltersHeight="floatingFiltersHeight"
        :localeText="localeText"
        :frameworkComponents="tableFrameworkComponents"
        :suppressCellSelection="suppressCellSelection"
        @selectionChanged="$emit('selectionChanged', $event)"
        :rowSelection='rowSelection ? rowSelection : $listeners.selectionChanged ? "single" : "none"'
        :suppressRowClickSelection="true"
        :domLayout="autoHeight? 'autoHeight' : 'normal'"
      />
      <!--        rowSelection="multiple"-->
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-community/src/styles/ag-grid.scss";

import InputFilter         from "./filters/InputFilter";
import InputFloatingFilter from "./floating-filters/InputFilter";

import DateFilter         from "./filters/DateFilter";
import DateFloatingFilter from "./floating-filters/DateFilter";

import SelectFilter         from "./filters/SelectFilter";
import SelectFloatingFilter from "./floating-filters/SelectFilter";

const LOCALE_TEXT = {
  filterOoo:          'Фильтр...',
  equals:             'Равно',
  notEqual:           'Не равно',
  lessThan:           'Меньше чем',
  greaterThan:        'Больше чем',
  lessThanOrEqual:    'Меньше или равно',
  greaterThanOrEqual: 'Больше или равно',
  inRange:            'В списке',
  contains:           'Содержит',
  notContains:        'Не содержит',
  startsWith:         'Начинается с',
  endsWith:           'Заканчивается',
  andCondition:       'И',
  orCondition:        'ИЛИ',
};

export default {
  name:       "AgGridTable",
  components: {
    AgGridVue,
  },
  props:      {
    autoHeight:            {
      default: false,
      type:    Boolean,
    },
    floatingFiltersHeight: {
      type:    Number,
      default: 35,
    },
    headerHeight:          {
      type:    Number,
      default: 35,
    },
    rowHeight:             {
      type:    Number,
      default: 35,
    },
    getRowHeight:          {
      type: Function,
    },
    columns:               {},
    filterable:            {
      default: true,
      type:    Boolean,
    },
    sortable:              {
      default: true,
      type:    Boolean,
    },
    getData:               {
      type:     Function,
      required: true,
    },
    noDataText:            {
      type:    String,
      default: "Данных нет",
    },
    suppressCellSelection: {
      default: true,
      type:    Boolean,
    },
    frameworkComponents:   {
      default: null,
      type:    Object,
    },
    rowSelection:          {
      default: null,
      type:    String,
    },
  },
  data() {
    return {
      tableFrameworkComponents: null,
      localeText:               LOCALE_TEXT,
      columnDefs:               null,
      gridOptions:              null,
      defaultColDef:            null,
      overlayLoadingTemplate:   null,
      overlayNoRowsTemplate:    null,
      tableHeight:              0,
      rows:                     null,
    };
  },
  computed: {
    rowClassRules() {
      return {
        'ag-table-row-pined_top':    function ( params ) {
          if (params.node.rowPinned && params.node.rowPinned.toLowerCase() === 'top') {
            return true;
          }
        },
        'ag-table-row-pined_bottom': function ( params ) {
          if (params.node.rowPinned && params.node.rowPinned.toLowerCase() === 'bottom') {
            return true;
          }
        },
        'ag-table-row-pined':        function ( params ) {
          if (params.node.rowPinned) {
            return true;
          }
        },
      };
    },
    tableStyles() {
      return {
        height: this.tableHeight,
      };
    },
  },
  methods:  {
    exportRows( fileName = 'Экспорт' ) {
      const columnKeys = this.$refs.table.columnDefs.map(c => c.colId).filter(key => key !== 'actions');
      this.gridOptions.api.exportDataAsCsv({
        fileName:            fileName,
        columnKeys,
        columnSeparator:     ';',
        processCellCallback: ( params ) => {
          let value = params.value;
          if (params.column.colDef && params.column.colDef.valueGetter) {
            let cellParams = {
              data:      params.node.data, // the data you provided for this row
              node:      params.node, // the row node for this row
              colDef:    params.column.colDef, // the column def for this column
              column:    params.column, // the column for this column
              api:       params.api, // the grid API
              columnApi: params.gridApi, // the grid Column API
              context:   params.context,  // the context,
              forExport: true,
            };
            value = params.column.colDef.valueGetter(cellParams, true);
          }

          return value ? value.toLocaleString() : value;
        },
      });
    },
    defaultCellRenderer( params, withLoader = false, callback ) {
      if (params.value !== undefined) {
        if (callback) {
          return `<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">${callback(params)}</div></div>`;
        } else if (params.value !== null) {
          return `<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">${params.value}</div></div>`;
        }
      } else if (params.column.colDef.field === 'account_number') {
        return '<div class="ag-table-cell-value-wrapper"><div class="ag-table-cell-value">Загрузка...</div></div>';
      } else {
        return "";
      }
    },
    mapColumns() {
      let loadingCount = 0;

      this.columnDefs = this.columns.map(column => {
        if ((!column.cellRendererFramework && column.cellRenderer === undefined) || typeof column.cellRenderer === "function") {
          let columnCellRenderer = column.cellRenderer;
          column.cellRenderer = ( params ) => {
            return this.defaultCellRenderer(params, loadingCount === 0, columnCellRenderer);
          };
          loadingCount++;
        }

        if ((this.filterable && column.filter === undefined) || column.filter !== undefined && column.filter !== false) {
          let filterName = typeof column.filter === 'string' ? column.filter.trim().toLowerCase() : 'text';

          let filterComponent = null;
          let filterComponentParams = {
            localeText:             this.localeText,
            suppressAndOrCondition: true,
            applyButton:            true,
          };

          let floatingFilterComponent = null;
          let floatingFilterComponentParams = {};

          switch (filterName) {
            case 'date':
            case 'datefilter':
              filterComponent = 'agDateFilter';
              floatingFilterComponent = 'agDateFloatingFilter';
              floatingFilterComponentParams.suppressFilterButton = true;
              filterComponentParams.filterOptions = ['equals'];
              filterComponentParams.defaultType = 'equals';
              break;
            case 'select':
            case 'selectfilter':
              filterComponent = 'agSelectFilter';
              floatingFilterComponent = 'agSelectFloatingFilter';
              floatingFilterComponentParams.suppressFilterButton = true;
              filterComponentParams.filterOptions = ['equals'];
              filterComponentParams.defaultType = 'equals';
              break;
            case 'number':
            case 'numberfilter':
              filterComponent = 'agInputFilter';
              floatingFilterComponent = 'agInputFloatingFilter';
              filterComponentParams.filterOptions = ['equals', 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual'];
              filterComponentParams.inputType = 'double';
              filterComponentParams.defaultType = 'equals';
              break;
            case 'text':
            case 'textFilter':
            default:
              filterComponent = 'agInputFilter';
              floatingFilterComponent = 'agInputFloatingFilter';
              filterComponentParams.filterOptions = ['equals', 'notEqual', 'contains', 'notContains', 'startsWith', 'endsWith'];
              filterComponentParams.inputType = 'text';
              filterComponentParams.defaultType = 'contains';
              break;
          }

          column.filter = filterComponent;
          column.filterParams = Object.assign({}, column.filterParams, filterComponentParams);
          column.floatingFilterComponent = floatingFilterComponent;
          column.floatingFilterComponentParams = Object.assign({}, column.floatingFilterComponentParams, floatingFilterComponentParams);
        }

        return column;
      });
    },
    updateRows() {
      this.gridApi.hideOverlay();
      this.rows = null;

      setTimeout(() => {
        this.gridApi.showLoadingOverlay();

        this.getData().then(( data ) => {
          this.rows = data;
          if (this.rows.length) {
            this.gridApi.hideOverlay();
          } else {
            this.gridApi.showNoRowsOverlay();
          }
        }).catch(e => console.error(e));
      }, 0);
    },
    updateHeaderHeight() {
      let header = this.$refs.tableWrapper.querySelector('.ag-header');
      let overlay = this.$refs.tableWrapper.querySelector('.ag-overlay');
      let headerHeight = header.getBoundingClientRect().height;
      overlay.style.height = `calc(100% - ${headerHeight}px)`;
      overlay.style.top = `${headerHeight}px`;
    },
  },
  beforeMount() {
    this.tableFrameworkComponents = {
      agDateFilter:           DateFilter,
      agDateFloatingFilter:   DateFloatingFilter,
      agSelectFilter:         SelectFilter,
      agSelectFloatingFilter: SelectFloatingFilter,
      agInputFilter:          InputFilter,
      agInputFloatingFilter:  InputFloatingFilter,
      ...this.frameworkComponents,
    };
    this.gridOptions = {};
    this.defaultColDef = {
      filter:    this.filterable,
      sortable:  this.sortable,
      resizable: true,
      flex:      1,
      minWidth:  50,
    };
    this.overlayLoadingTemplate =
      '<div class="ag-grid-infinite-wrapper__loading-overlay"><div class="block-loading"></div><div class="ag-grid-infinite-wrapper__loading-overlay-text">Загрузка...</div></div>';
    this.overlayNoRowsTemplate = `<div class="ag-grid-infinite-wrapper__no-rows">${this.noDataText}</div>`;
    this.mapColumns();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.tableHeight = this.$refs.tableWrapper.clientHeight;
    this.updateRows();
    this.updateHeaderHeight();
    this.$emit('grid-ready', this.gridApi);
  },
};
</script>

<style lang="scss">
.ag-grid-infinite-wrapper {
  flex: 1;
  display: flex;
  flex-flow: column nowrap;

  &__loading-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .block-loading {
      &::before {
        width: 30px;
        height: 30px;
      }
    }

    &-text {
      text-align: center;
      color: rgba(var(--app-primary), 1);
      margin-top: 15px;
      font-weight: 500;
      font-size: 16px;
    }
  }

  &__no-rows {
    font-weight: 500;
    font-size: 1.2rem;
  }
}

.ag-grid-infinite-table-wrapper {
  flex: 1;
  display: flex;
  flex-flow: column nowrap;

  .ag-row {
    border-bottom: 1px solid rgba(0, 0, 0, .1);
  }

  .ag-header-cell {
    &.checkbox {
      justify-content: flex-start;
      text-align: right;
      flex-direction: row-reverse;
    }
  }

  .ag-cell {
    user-select: text;

    &.checkbox {
      .ag-cell-wrapper {
        padding: 0 10px;
        justify-content: flex-start;
        text-align: right;
        flex-direction: row-reverse;
      }
    }
  }
}

.ag-grid-infinite-table {
  flex: 1;


  .ag-row-odd {
    background: #f5f8fa;
  }

  .ag-header-cell-label {
    justify-content: center;
  }

  .ag-header-icon {
    margin-left: 5px;
  }

  .ag-icon {
    width: 16px;
    height: 16px;
    background-position: center;
    background-size: contain;
  }

  .ag-floating-filter-button-button {
    appearance: none;
    background: transparent;
    border: none;
    box-shadow: none;
    cursor: pointer;
    margin-left: 5px;
  }

  .ag-menu {
    box-shadow: 5px 5px 10px rgba(0, 0, 0, .3);
    background: #fff;
    border-radius: 2px;
  }

  .ag-row-selected {
    background: rgba(139, 212, 238, .2);
  }

  .ag-header-cell-menu-button {
    display: none;
  }

  .ag-pinned-left-cols-container, .ag-pinned-left-header {
    border-right: 1px solid rgba(0, 0, 0, .1);
    min-height: 100%;
  }

  .ag-pinned-left-floating-bottom {
    border-right: 1px solid rgba(0, 0, 0, .1);
  }

  .ag-floating-bottom {
    border-top: 1px solid rgba(0, 0, 0, .1);
  }

  .ag-icon-filter {
    background-image: url('~@/assets/images/ag-grid/dark-filter.svg');
  }

  .ag-icon-asc {
    background-image: url('~@/assets/images/ag-grid/dark-asc.svg');
  }

  .ag-icon-desc {
    background-image: url('~@/assets/images/ag-grid/dark-desc.svg');
  }

  .ag-table-row-pined {
    font-weight: 600;
  }

  .ag-table-cell-tag {
    padding: 5px;
    font-size: 10px;
    line-height: 1.1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ag-cell-value-link {
    color: rgba(var(--app-content-color), 1);
    cursor: pointer;

    &:hover {
      color: rgba(var(--app-primary), 1);
      text-decoration: underline;
    }
  }
}

.ag-header-cell {
  font-weight: bold;
  text-align: left;
  vertical-align: bottom;
  padding: 0 10px;
  background: transparent;
  font-size: 11px;
  border-bottom: 1px solid rgba(0, 0, 0, .125);

  &-text {
    white-space: normal;
    text-align: center;
    line-height: 1.2;
    font-size: 11px;
  }

  &.left {
    .ag-cell-label-container .ag-header-cell-label {
      //padding: 0 10px;
      justify-content: flex-start;
      text-align: left;

      .ag-header-cell-text {
        text-align: left;
      }
    }
  }
}

.ag-cell-value {
  padding: .5rem 10px;
  font-size: 12px;

  & > span {
    display: block;
    height: 100%;
  }

  &.left {
    .ag-table-cell-value {
      //padding: 0 10px;
      justify-content: flex-start;
      text-align: left;
    }
  }
}

.ag-table-cell-value-wrapper {
  display: block;
  height: 100%;
}

.ag-table-cell-value {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.1;
}

.ag-cell-auto-height {
  white-space: normal;
}

.ag-table-row-pined_bottom {
  .ag-cell-value > span {
    text-align: center;
  }
}

.ag-root.ag-layout-normal, .ag-root.ag-layout-auto-height {
  min-height: 180px;
}

.ag-selection-checkbox, .ag-header-select-all {
  margin-left: 5px;
}

.ag-checkbox-input-wrapper {
  //font-size: 16px;
  //line-height: 16px;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 20px;
  height: 20px;
  background-color: #fff;
  display: inline-block;
  vertical-align: middle;
  flex: none;
}

.ag-checkbox-input-wrapper::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: 3px;
  border: 2px solid rgb(180, 180, 180);
  box-sizing: border-box;
  @include anim();
}

.ag-checkbox-input-wrapper::after {
  font-family: "feather";
  content: "\e83f";
  position: absolute;
  font-size: 16px;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  line-height: 20px;
  text-align: center;
  opacity: 0;
  border-radius: 3px;
  padding-left: 1px;
  pointer-events: none;
  box-sizing: border-box;
  color: #fff;
  @include anim();
}

.ag-checkbox-input-wrapper.ag-checked::before {
  border-color: rgba(var(--vs-primary), 1);
  background: rgba(var(--vs-primary), 1);
}

.ag-checkbox-input-wrapper.ag-checked::after {
  opacity: 1;
}

.ag-checkbox-input-wrapper.ag-indeterminate::before {
  border-color: rgb(180, 180, 180);
  background: rgb(180, 180, 180);
}

.ag-checkbox-input-wrapper.ag-indeterminate::after {
  content: "\e897";
  opacity: 1;
}

.ag-checkbox-input-wrapper input, .ag-checkbox-input-wrapper input {
  position: absolute;
  -webkit-appearance: none;
  opacity: 0;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
}
</style>
